
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { FilmInventory, FilmFormValues, filmSchema, mapStatusToDatabase } from "@/types/inventory";
import { AddFilmForm } from "./AddFilmForm";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";

interface EditFilmDialogProps {
  film: FilmInventory;
  onEditComplete: () => void;
  trigger?: React.ReactNode;
}

export function EditFilmDialog({ film, onEditComplete, trigger }: EditFilmDialogProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const form = useForm<FilmFormValues>({
    resolver: zodResolver(filmSchema),
    defaultValues: {
      film_name: film.film_name,
      film_type: film.film_type,
      status: film.status,
      // Extract just the YYYY-MM part for the month input
      expiration_date: film.expiration_date ? film.expiration_date.substring(0, 7) : '',
      iso: film.iso,
      brand: film.brand,
      quantity: film.quantity,
      notes: film.notes || '',
    },
  });

  const onSubmit = async (values: FilmFormValues) => {
    try {
      // Format the expiration date properly for database storage
      let formattedExpirationDate: string | null = null;
      if (values.expiration_date) {
        const [year, month] = values.expiration_date.split('-');
        // Get the last day of the month
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        formattedExpirationDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;
      }

      // Get database status
      const databaseStatus = mapStatusToDatabase(values.status);
      
      // Make sure we have a valid ID
      if (!film.id) {
        toast.error(t('cannot_update_film_missing_id'));
        return;
      }

      // Using type assertions to match Supabase's expected types
      const { error } = await supabase
        .from('film_inventory')
        .update({
          film_name: values.film_name,
          film_type: values.film_type,
          status: databaseStatus,
          expiration_date: formattedExpirationDate,
          iso: values.iso,
          brand: values.brand,
          quantity: values.quantity,
          notes: values.notes || null,
        } as any)
        .eq('id', film.id);

      if (error) {
        console.error('Error updating film:', error);
        throw error;
      }

      toast.success(t('film_updated_successfully'));
      setOpen(false);
      onEditComplete();
    } catch (error: any) {
      toast.error(`${t('error_updating_film')}: ${error.message}`);
      console.error('Error:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            {t('edit')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className={isMobile ? "w-[95vw] max-w-[95vw] p-4" : "max-w-md"}>
        <DialogHeader>
          <DialogTitle>{t('edit_film')}</DialogTitle>
          <DialogDescription>
            {t('update_film_details')}
          </DialogDescription>
        </DialogHeader>
        <AddFilmForm form={form} onSubmit={onSubmit} isEditing={true} />
      </DialogContent>
    </Dialog>
  );
}
